/*
 *  -----------------------------------------------------------------------
 *    <copyright file="delete-snapshot.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import { useLazyQuery, useMutation } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState, useRef, useEffect } from 'react';

import { useTranslation, useBoolean } from 'hooks';
import { DELETE_SNAPSHOT, GET_DELETE_SNAPSHOT_STATUS } from 'queries/snapshots';
import NotificationService from 'utils/notification';
import { SaveButton } from 'views/components/core/buttons';
import { CustomDialog, DialogActions, DialogContent, DialogTitle } from 'views/components/core/dialog';
import TextField from 'views/components/core/text-field';

import './styles.scss';

interface IDeleteSnapshot {
    open: boolean;
    onClose: Function;
    snapshots: any[];
    snapshotName: string;
}

const DeleteSnapshot = ({ open, onClose, snapshots, snapshotName }: IDeleteSnapshot) => {
    const { t } = useTranslation();

    const [deleteSnapshot] = useMutation(DELETE_SNAPSHOT);
    const [loadSnapshotStatus, { data }] = useLazyQuery(GET_DELETE_SNAPSHOT_STATUS);

    const [name, setName] = useState('');
    const saving = useBoolean(false);
    const timeout = useRef(null);
    const disabledSave = useBoolean(true);
    const errorName = useBoolean(true);

    const close = (action?: string) => {
        onClose(action);

        if (action !== 'error') {
            errorName.setTrue();
            setName('');
            saving.setFalse();
            disabledSave.setTrue();
        }
    };

    useEffect(() => {
        if (data?.deleteSnapshotStatus.isFinished && !data?.deleteSnapshotStatus.error) {
            clearInterval(timeout.current);
            saving.setFalse();
            close('success');
        } else if (data?.deleteSnapshotStatus.error) {
            clearInterval(timeout.current);
            NotificationService.showNotification(data?.deleteSnapshotStatus.error);
            close();
        }
    }, [data]);

    const onChange = (event: any) => {
        disabledSave.setFalse();
        const { value } = event.target;

        setName(value);
        const isSnapshotExist = snapshots.find((item: any) => item.name === value);
        errorName.setValue(Boolean(!isSnapshotExist));
    };

    const onDelete = () => {
        saving.setTrue();
        deleteSnapshot({
            variables: {
                snapshotName: name,
            },
        })
            .then((result: any) => {
                if (result.data.deleteSnapshot) {
                    timeout.current = setInterval(() => {
                        loadSnapshotStatus({
                            variables: {
                                statusId: result.data.deleteSnapshot,
                            },
                        });
                    }, 2000);
                }
            })
            .catch((error: any) => {
                console.log('error deleting snapshot', error);
                saving.setFalse();
                close('error');
            });
    };

    return (
        <CustomDialog
            className="delete-snapshot-dialog"
            onClose={() => close()}
            fullWidth
            scroll="paper"
            maxWidth="sm"
            open={open}
        >
            <DialogTitle id="delete-snapshot-modal-title">
                {t('admin:deleteSnapshot')}
                <FontAwesomeIcon
                    icon={['fal', 'times']}
                    className="fa-lg close-icon-cv"
                    onClick={() => close()}
                    data-testid="close-dialog"
                />
            </DialogTitle>
            <DialogContent className="add-risk-dialog-content" style={{ padding: 15 }}>
                {!saving.value ? (
                    <>
                        <p className="info-text">{t('admin:areYouSureDeleteSnapshot', { name: snapshotName })}</p>
                        <p className="info-text">{t('admin:enterSnapshotName')}</p>
                        <TextField
                            className="mb-20"
                            id="snapshot-name"
                            label={`${t('common:name')}*`}
                            value={name}
                            placeholder={`${t('admin:enterSnapshotNameToConfirm')}*`}
                            fullWidth
                            error={errorName.value}
                            variant="outlined"
                            onChange={onChange}
                        />
                    </>
                ) : (
                    <p className="loading-state-text">{t('admin:deletingSnapshot')}</p>
                )}
                <DialogActions style={{ padding: 0 }}>
                    <SaveButton
                        disabled={disabledSave.value || errorName.value || saving.value}
                        text={t('common:delete')}
                        onClick={onDelete}
                        id="delete-snapshot-btn"
                    />
                </DialogActions>
            </DialogContent>
        </CustomDialog>
    );
};

export default DeleteSnapshot;
